<script setup lang="ts">
const { $getTranslate } = useNuxtApp();
const Logo = defineAsyncComponent(
  () => import("~/assets/svg/logo_facebook.svg"),
);

const props = defineProps({
  appId: {
    type: String,
    required: true,
  },
});
const emit = defineEmits(["success", "error"]);

// Cached per-instance: prevents double-init on repeated clicks.
// window.FB check covers the case where SDK was already loaded (e.g. after unmount/remount).
let fbReadyPromise: Promise<void> | null = null;

// Mirror the Google button: cap how long we wait for the SDK so a stalled or
// neutered (privacy-extension) sdk.js surfaces as emit("error") instead of
// hanging — script.onerror alone only covers an outright failed network request.
const FACEBOOK_SDK_LOAD_TIMEOUT = 5000;

// Disables the button for the whole load+login round-trip so a fast double-click
// can't stack two FB.login() popups. We can't disable-until-loaded like Google,
// because the button click is what triggers the lazy load in the first place.
const isProcessing = ref(false);

// Component-scoped so onBeforeUnmount can clear a still-pending load timer
// (mirrors ButtonLoginGoogle's teardown). Otherwise a stale timeout fires fail()
// after the component is gone and mutates the shared #facebook-jssdk <script>.
let sdkLoadTimeoutId: ReturnType<typeof setTimeout> | undefined;

const ensureFBSDK = (): Promise<void> => {
  if (fbReadyPromise) return fbReadyPromise;

  fbReadyPromise = new Promise<void>((resolve, reject) => {
    if (window.FB) {
      resolve();
      return;
    }

    // Single bail-out path: drop the dead <script> so the next click inserts a
    // fresh one (with a fresh onerror), clear the timer, and allow a retry.
    const fail = (message: string) => {
      clearTimeout(sdkLoadTimeoutId);
      document.getElementById("facebook-jssdk")?.remove();
      fbReadyPromise = null;
      reject(new Error(message));
    };

    // Resolve only after fbAsyncInit fires — FB is genuinely ready at that point.
    window.fbAsyncInit = () => {
      if (!window.FB) {
        fail("Facebook SDK loaded without FB global");
        return;
      }
      clearTimeout(sdkLoadTimeoutId);
      window.FB.init({
        appId: props.appId,
        cookie: false,
        xfbml: false,
        version: "v25.0",
      });
      resolve();
    };

    // Insert the SDK script only if it hasn't been added yet (e.g. after a failed load or a previous mount/unmount).
    if (!document.getElementById("facebook-jssdk")) {
      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.onerror = () => fail("Facebook SDK failed to load");
      document.head.appendChild(script);
    }

    // Timeout: if the SDK doesn't load in time, reject and allow a retry.
    sdkLoadTimeoutId = setTimeout(
      () => fail("Facebook SDK load timed out"),
      FACEBOOK_SDK_LOAD_TIMEOUT,
    );
  });

  return fbReadyPromise;
};

// Shared bail-out for the load and pre-login failure paths: re-enable the button
// and surface the error to the parent. (The FB.login callback below resets
// isProcessing itself because it branches between success and error.)
const failLogin = () => {
  isProcessing.value = false;
  emit("error");
};

const logInWithFacebook = async () => {
  if (isProcessing.value) return;

  // No appId (e.g. env not configured) → behave like the Google button: do nothing.
  // Don't disable or surface a user-facing error for what is a deployment config gap.
  if (!props.appId) {
    console.warn("Facebook login unavailable: missing FACEBOOK_APP_ID");
    return;
  }

  isProcessing.value = true;

  try {
    await ensureFBSDK();
  } catch (e) {
    console.error("Facebook SDK error:", e);
    failLogin();
    return;
  }

  if (!window.FB) {
    failLogin();
    return;
  }

  window.FB.login((response) => {
    // Fires on success AND on user-dismiss/deny (no authResponse) — reset in both
    // so the button never stays permanently disabled after a cancelled popup.
    isProcessing.value = false;
    if (response.authResponse) emit("success", response);
    else emit("error");
  });
};

// Clear a still-pending SDK load timer if the component unmounts mid-load (e.g.
// the login modal is closed), so it can't fire fail() and remove the shared
// #facebook-jssdk <script> after teardown.
onBeforeUnmount(() => clearTimeout(sdkLoadTimeoutId));
</script>

<template>
  <button
    type="button"
    :disabled="isProcessing"
    @click.prevent="logInWithFacebook"
  >
    <component :is="Logo" class="w-6 h-6" />
    <span class="font-bold text-white text-sm 3xl:text-xl leading-6">{{
      $getTranslate("login_form_button_facebook")
    }}</span>
  </button>
</template>

<style scoped lang="postcss">
button {
  @apply flex py-2.5 px-4 lg:py-3 3xl:py-4 items-center transition-colors duration-200 justify-center gap-4 w-full border border-[#1877F2] rounded bg-[#1877F2] hover:bg-blue-500 active:bg-blue-500 max-h-9 lg:max-h-10 3xl:max-h-11 disabled:opacity-70 disabled:cursor-not-allowed;
}
button span {
  font-family: Helvetica;
}
</style>
